import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, Copy, Navigation, Plus } from "lucide-react";
import dayjs from "dayjs";

import {
  Avatar,
  Button,
  DateTimeInput,
  SpotPicker,
  getInitials,
  tintForId,
  useConfirm,
} from "@/ui";
import { useAuth } from "@/contexts/AuthContext";
import { useSpots } from "@/features/queries";
import { haptic } from "@/lib/telegram";
import {
  playerName,
  useBoardAction,
  useGame,
  type Game,
  type GameOption,
  type GamePlayer,
  type PlaySpot,
} from "@/features/play/api";
import { BackHeader, Card, ErrorBox, Muted } from "@/features/play/parts";
import { formatTime } from "@/features/play/format";

/**
 * Game card + open negotiation board. Everyone votes for one spot and one
 * time and sees everyone else's votes live (the board polls); once both
 * of a pair (a majority of a company) pick the same spot and time, the
 * backend schedules the game.
 */
export function GameBoardPage() {
  const { id } = useParams();
  const gameId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const game = useGame(Number.isFinite(gameId) ? gameId : null);
  const action = useBoardAction(gameId);
  const confirm = useConfirm();

  if (!user) return null;
  const g = game.data;

  const goBack = () => {
    // Opened straight from a bot message there is no in-app history.
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate("/", { replace: true });
  };

  const leave = async () => {
    const ok = await confirm({
      title: g?.status === "scheduled" ? "Выйти из игры?" : "Тебе не подходит?",
      description:
        g?.status === "scheduled"
          ? "Остальным сообщим. Если останется один игрок — игра отменится."
          : "Ты выйдешь из этой игры, а поиск продолжится. Этих же соперников больше не предложим.",
      confirmLabel: "Выйти",
      tone: "danger",
    });
    if (ok) action.mutate({ kind: "leave" });
  };

  return (
    <div style={{ paddingBottom: 24 }}>
      <BackHeader title={g?.mode === "rated" ? "Игра на рейтинг" : "Игра"} onBack={goBack} />

      {game.isLoading && <div className="body" style={{ padding: 16 }}>Загрузка…</div>}
      {game.error && <ErrorBox error={game.error} />}

      {g && (
        <>
          {g.my_status === "invited" && <InviteCard g={g} onAnswer={(accept) => action.mutate({ kind: "invite", accept })} busy={action.isPending} />}

          {g.status === "scheduled" && <ScheduledCard g={g} />}
          {g.status === "cancelled" && (
            <Card><div className="body">Игра отменена.</div><Muted>Если поиск ещё идёт — найдём новую.</Muted></Card>
          )}
          {g.status === "completed" && <Card><div className="body">Игра прошла.</div></Card>}
          {(g.my_status === "left" || g.my_status === "declined") && g.status !== "cancelled" && (
            <Card><div className="body">Ты вышел из этой игры.</div></Card>
          )}

          <Players g={g} meId={user.id} />

          {g.status === "negotiating" && g.my_status === "active" && (
            <Board g={g} meId={user.id} onAction={action.mutate} busy={action.isPending} />
          )}
          {g.status === "negotiating" && g.my_status === "invited" && <ReadOnlyOptions g={g} />}

          <ErrorBox error={action.error} />

          {g.my_status === "active" && (g.status === "negotiating" || g.status === "scheduled") && (
            <div style={{ padding: "12px 16px" }}>
              <Button fullWidth variant="danger" onClick={leave} disabled={action.isPending}>
                {g.status === "scheduled" ? "Выйти из игры" : "Мне не подходит"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------- pieces ----------------------------------------------------------

function InviteCard({ g, onAnswer, busy }: { g: Game; onAnswer: (accept: boolean) => void; busy: boolean }) {
  const n = g.participants.filter((p) => p.status === "active").length;
  return (
    <Card accent>
      <div className="body" style={{ fontWeight: 600 }}>Рядом собирается игра — присоединишься?</div>
      <Muted>
        {g.status === "scheduled" && g.spot && g.scheduled_at
          ? `${g.spot.name} · ${formatTime(g.scheduled_at)} · уже ${n} чел.`
          : `Компания из ${n} чел. ещё выбирает спот и время — сможешь проголосовать.`}
      </Muted>
      {g.my_invite_deadline && <Muted>Ответь до {formatTime(g.my_invite_deadline)}.</Muted>}
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <Button fullWidth size="sm" onClick={() => onAnswer(true)} disabled={busy}>Присоединиться</Button>
        <Button fullWidth size="sm" variant="secondary" onClick={() => onAnswer(false)} disabled={busy}>
          Не пойду
        </Button>
      </div>
    </Card>
  );
}

function ScheduledCard({ g }: { g: Game }) {
  if (!g.spot || !g.scheduled_at) return null;
  return (
    <Card accent>
      <div className="caption" style={{ color: "var(--brand-orange)", fontWeight: 600 }}>Игра назначена</div>
      <div className="h2" style={{ margin: "6px 0 2px", fontSize: 22 }}>{g.spot.name}</div>
      <div className="body">{formatTime(g.scheduled_at)}</div>
      <SpotDirections spot={g.spot} />
      {g.mode === "rated" && <Muted>После игры бот спросит, кто победил, и обновит рейтинг.</Muted>}
      {g.mode === "casual" && g.open_to_join && (
        <Muted>Игра открыта — к вам могут присоединиться игроки поблизости.</Muted>
      )}
    </Card>
  );
}

/** Address, tap-to-copy coordinates and a compact Yandex Maps route button. */
function SpotDirections({ spot }: { spot: PlaySpot }) {
  const [copied, setCopied] = useState(false);
  const coords = `${spot.latitude.toFixed(6)}, ${spot.longitude.toFixed(6)}`;

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(t);
  }, [copied]);

  const copy = async () => {
    if (await copyText(coords)) {
      haptic("light");
      setCopied(true);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8, marginTop: 10 }}>
      {spot.address && <div className="body" style={{ color: "var(--fg2)" }}>{spot.address}</div>}
      <button
        type="button"
        onClick={copy}
        aria-label={`Скопировать координаты ${coords}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 10px",
          background: "var(--bg-section)",
          border: "1px solid var(--separator)",
          borderRadius: 8,
          color: "var(--fg1)",
          fontFamily: "var(--font-mono)",
          fontSize: 13,
          cursor: "pointer",
        }}
      >
        {coords}
        {copied ? (
          <Check size={14} color="var(--brand-orange)" aria-hidden="true" />
        ) : (
          <Copy size={14} color="var(--fg3)" aria-hidden="true" />
        )}
      </button>
      <a
        href={`https://yandex.ru/maps/?rtext=~${spot.latitude},${spot.longitude}`}
        target="_blank"
        rel="noreferrer"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 14px",
          background: "var(--brand-orange)",
          color: "#fff",
          fontSize: 14,
          fontWeight: 600,
          borderRadius: 999,
          textDecoration: "none",
        }}
      >
        <Navigation size={14} aria-hidden="true" />
        Маршрут в Яндекс Картах
      </a>
    </div>
  );
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older WebViews: no async clipboard API.
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  }
}

function votedFor(g: Game, userId: number, kind: GameOption["kind"]): GameOption | undefined {
  return g.options.find((o) => o.kind === kind && o.marked_by.includes(userId));
}

function Players({ g, meId }: { g: Game; meId: number }) {
  return (
    <>
      <div className="section-header" style={{ padding: "18px 16px 8px" }}>
        Участники · {g.participants.filter((p) => p.status === "active").length}
      </div>
      <div style={{ background: "var(--bg-section)", borderRadius: "var(--r-lg)", margin: "0 16px" }}>
        {g.participants.map((p) => {
          const chose = !!votedFor(g, p.id, "spot") && !!votedFor(g, p.id, "time");
          return (
            <div
              key={p.id}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px" }}
            >
              <Avatar initials={getInitials(p.first_name, p.last_name, p.username)} size={36} tint={tintForId(p.id)} src={p.photo_url} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="body" style={{ fontWeight: 500 }}>
                  {playerName(p)}{p.id === meId && " · ты"}
                </div>
                <div className="caption" style={{ color: "var(--fg3)" }}>
                  {p.username ? (
                    <a href={`https://t.me/${p.username}`} target="_blank" rel="noreferrer" style={{ color: "var(--brand-orange)", textDecoration: "none" }}>
                      @{p.username}
                    </a>
                  ) : (
                    "ника нет — договаривайтесь на доске"
                  )}
                  {g.mode === "rated" && ` · Elo ${p.rating}`}
                  {p.status === "invited" && " · приглашён"}
                </div>
              </div>
              {g.status === "negotiating" && p.status === "active" && chose && (
                <Check size={18} color="var(--brand-orange)" aria-label="выбрал спот и время" />
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

type Act = ReturnType<typeof useBoardAction>["mutate"];

function Board({ g, meId, onAction, busy }: { g: Game; meId: number; onAction: Act; busy: boolean }) {
  const byId = useMemo(() => new Map(g.participants.map((p) => [p.id, p])), [g.participants]);
  const active = g.participants.filter((p) => p.status === "active");
  const spots = g.options.filter((o) => o.kind === "spot");
  const times = g.options.filter((o) => o.kind === "time");
  const missing = [
    !votedFor(g, meId, "spot") && "спот",
    !votedFor(g, meId, "time") && "время",
  ].filter(Boolean);

  return (
    <>
      <Card>
        <div className="body" style={{ fontWeight: 600 }}>Выберите один спот и одно время</div>
        <Muted>
          {active.length > 2
            ? `Решает большинство: нужно ${g.required} из ${active.length} голосов.`
            : "Игра назначится, как только вы оба выберете одно и то же."}{" "}
          Чужой выбор видно сразу — можно подстроиться или предложить своё.
        </Muted>
        <Muted>
          {missing.length > 0
            ? `Тебе осталось выбрать: ${missing.join(" и ")}.`
            : "Твой выбор сделан — ждём остальных."}
        </Muted>
        {g.negotiation_deadline && (
          <Muted>Договориться нужно до {formatTime(g.negotiation_deadline)}.</Muted>
        )}
        <LiveHint />
      </Card>

      <OptionSection
        title="Где"
        options={spots}
        need={g.required}
        meId={meId}
        byId={byId}
        label={(o) => o.spot?.name ?? "—"}
        sub={(o) => o.spot?.address ?? ""}
        onPick={(o, on) => onAction({ kind: "mark", option_id: o.id, on })}
        busy={busy}
      />
      <AddSpot g={g} onAdd={(spot_id) => onAction({ kind: "add-spot", spot_id })} busy={busy} />

      <OptionSection
        title="Когда"
        options={times}
        need={g.required}
        meId={meId}
        byId={byId}
        label={(o) => (o.starts_at ? formatTime(o.starts_at) : "—")}
        sub={() => ""}
        onPick={(o, on) => onAction({ kind: "mark", option_id: o.id, on })}
        busy={busy}
      />
      <AddTime onAdd={(starts_at) => onAction({ kind: "add-time", starts_at })} busy={busy} />
    </>
  );
}

function LiveHint() {
  return (
    <div className="caption" style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, color: "var(--fg3)" }}>
      <span
        aria-hidden="true"
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "var(--brand-court)",
          animation: "sp-fade-in 1.2s ease-in-out infinite alternate",
        }}
      />
      Доска обновляется сама
    </div>
  );
}

function OptionSection({
  title,
  options,
  need,
  meId,
  byId,
  label,
  sub,
  onPick,
  busy,
}: {
  title: string;
  options: GameOption[];
  need: number;
  meId: number;
  byId: Map<number, GamePlayer>;
  label: (o: GameOption) => string;
  sub: (o: GameOption) => string;
  onPick: (o: GameOption, on: boolean) => void;
  busy: boolean;
}) {
  return (
    <>
      <div className="section-header" style={{ padding: "18px 16px 8px" }}>{title} · выбери один</div>
      <div role="radiogroup" aria-label={title} style={{ background: "var(--bg-section)", borderRadius: "var(--r-lg)", margin: "0 16px" }}>
        {options.map((o) => {
          const mine = o.marked_by.includes(meId);
          const voters = o.marked_by.map((uid) => byId.get(uid)).filter((p): p is GamePlayer => !!p);
          const names = voters.map((p) => (p.id === meId ? "ты" : playerName(p))).join(", ");
          const enough = voters.length >= need;
          return (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={mine}
              disabled={busy}
              // Tapping my own pick withdraws the vote; any other moves it.
              onClick={() => onPick(o, !mine)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                width: "100%",
                padding: "12px 16px",
                background: "transparent",
                border: 0,
                borderBottom: "1px solid var(--separator)",
                textAlign: "left",
                cursor: "pointer",
                color: "var(--fg1)",
                fontFamily: "var(--font-body)",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 22,
                  height: 22,
                  flex: "none",
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  border: mine ? "0" : "2px solid var(--separator)",
                  background: mine ? "var(--brand-orange)" : "transparent",
                  color: "#fff",
                }}
              >
                {mine && <Check size={14} />}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="body" style={{ display: "block", fontWeight: 500 }}>{label(o)}</span>
                {sub(o) && <span className="caption" style={{ display: "block", color: "var(--fg3)" }}>{sub(o)}</span>}
                <span style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                  {voters.length > 0 && (
                    <span style={{ display: "inline-flex" }}>
                      {voters.map((p, i) => (
                        <span key={p.id} style={{ marginLeft: i ? -6 : 0, borderRadius: "50%", boxShadow: "0 0 0 2px var(--bg-section)" }}>
                          <Avatar initials={getInitials(p.first_name, p.last_name, p.username)} size={20} tint={tintForId(p.id)} src={p.photo_url} />
                        </span>
                      ))}
                    </span>
                  )}
                  <span className="caption" style={{ color: names ? "var(--fg2)" : "var(--fg3)" }}>
                    {names ? `Выбрали: ${names}` : "Пока никто не выбрал"}
                  </span>
                </span>
              </span>
              <span
                className="caption"
                style={{
                  flex: "none",
                  fontWeight: 600,
                  color: enough ? "var(--brand-orange)" : "var(--fg3)",
                }}
              >
                {voters.length}/{need}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}

function AddSpot({ g, onAdd, busy }: { g: Game; onAdd: (id: number) => void; busy: boolean }) {
  const [open, setOpen] = useState(false);
  const { data: spots = [] } = useSpots();
  const taken = g.options.filter((o) => o.kind === "spot" && o.spot).map((o) => o.spot!.id);
  const playable = useMemo(() => spots.filter((s) => s.place_type !== "store"), [spots]);

  if (!open) {
    return (
      <div style={{ padding: "8px 16px" }}>
        <Button size="sm" variant="ghost" leading={<Plus size={16} />} onClick={() => setOpen(true)}>
          Предложить другой спот
        </Button>
      </div>
    );
  }
  return (
    <div style={{ padding: "8px 16px" }}>
      <SpotPicker
        spots={playable}
        value={null}
        excludeIds={taken}
        emptyLabel={null}
        closedPlaceholder="Выбери спот поблизости"
        onChange={(id) => {
          if (id !== null) onAdd(id);
          setOpen(false);
        }}
      />
      <Muted>Не дальше 8 км от спотов на доске. Твой голос перейдёт на него, остальным придёт сообщение.</Muted>
      {busy && <Muted>Сохраняем…</Muted>}
    </div>
  );
}

function AddTime({ onAdd, busy }: { onAdd: (iso: string) => void; busy: boolean }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string | null>(() =>
    dayjs().add(2, "hour").minute(0).second(0).toISOString(),
  );
  if (!open) {
    return (
      <div style={{ padding: "8px 16px" }}>
        <Button size="sm" variant="ghost" leading={<Plus size={16} />} onClick={() => setOpen(true)}>
          Предложить другое время
        </Button>
      </div>
    );
  }
  return (
    <div style={{ padding: "8px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
      <DateTimeInput value={value} onChange={setValue} min={dayjs().toISOString()} required />
      <div style={{ display: "flex", gap: 8 }}>
        <Button
          fullWidth
          size="sm"
          disabled={!value || busy}
          onClick={() => {
            if (value) onAdd(value);
            setOpen(false);
          }}
        >
          Предложить
        </Button>
        <Button fullWidth size="sm" variant="secondary" onClick={() => setOpen(false)}>Отмена</Button>
      </div>
    </div>
  );
}

function ReadOnlyOptions({ g }: { g: Game }) {
  const spots = g.options.filter((o) => o.kind === "spot" && o.spot).map((o) => o.spot!.name);
  const times = g.options.filter((o) => o.kind === "time" && o.starts_at).map((o) => formatTime(o.starts_at!));
  return (
    <Card>
      <div className="body" style={{ fontWeight: 600 }}>Что обсуждают</div>
      <Muted>Споты: {spots.join(", ") || "—"}</Muted>
      <Muted>Время: {times.join(", ") || "—"}</Muted>
    </Card>
  );
}
